import { useEffect } from 'react'

import { Code } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { LicenseType } from '@ebalance/proto/bfftypes/v1/license_type_pb'
import { useTranslation } from 'react-i18next'

import { CrudModalLayout } from '~/components/common/CrudModalLayout'
import { useCrudModalForm } from '~/hooks/useCrudModalForm'
import { todayCivilDate } from '~/models/civilDate'
import { SubmitterMasterFormFields } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/components/SubmitterMasterFormFields'
import { useCreateSubmitterMaster } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/hooks/useCreateSubmitterMaster'
import {
  type SubmitterMasterFormInput,
  SubmitterMasterFormSchema,
  type SubmitterMasterFormValues,
} from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/schema'
import { resolveSubmitterMasterDetailedError } from '~/pages/AdminOcctoMaster/tabs/SubmitterMaster/models/serverError'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const SUBMITTER_ERROR_KEYS: Partial<Record<Code, string>> = {
  [Code.AlreadyExists]:
    'page:adminOcctoMaster.submitterMaster.serverError.alreadyExists',
  [Code.Aborted]: 'page:adminOcctoMaster.submitterMaster.serverError.aborted',
  [Code.NotFound]: 'page:adminOcctoMaster.submitterMaster.serverError.notFound',
  [Code.InvalidArgument]:
    'page:adminOcctoMaster.submitterMaster.serverError.invalidArgument',
}

export const CreateSubmitterMasterModal = ({
  isOpen,
  onClose,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { mutateAsync, isPending } = useCreateSubmitterMaster()

  const {
    control,
    errors,
    serverError,
    handleClose,
    handleSave,
    setValue,
    isDirty,
  } = useCrudModalForm<SubmitterMasterFormInput, SubmitterMasterFormValues>({
    // モーダルオープン時に validFrom を実行日に更新（defaultValues の todayCivilDate() は
    // コンポーネントマウント時に固定化されるため、日跨ぎで開き直しても前日のままになる）。
    // useCrudModalForm は isOpen を受け取らないので、呼び出し側で useEffect で更新する。
    schema: SubmitterMasterFormSchema,
    defaultValues: {
      submitterCode: '',
      submitterName: '',
      licenseType: LicenseType.GENERATION,
      areaCode: AreaCode.TOKYO,
      businessMasterId: '',
      note: '',
      validFrom: todayCivilDate(),
      validTo: undefined,
      members: [],
    },
    onSubmit: async (values) => {
      await mutateAsync({
        submitterCode: values.submitterCode,
        submitterName: values.submitterName,
        licenseType: values.licenseType,
        areaCode: values.areaCode,
        businessMasterId: values.businessMasterId,
        note:
          values.note !== undefined && values.note !== ''
            ? values.note
            : undefined,
        validFrom: values.validFrom,
        validTo: values.validTo,
        members: values.members.map((m) => ({
          bgMasterId: m.bgMasterId,
          bgName: m.bgName,
          validFrom: m.validFrom,
          validTo: m.validTo,
        })),
      })
    },
    onClose,
    customErrorKeys: SUBMITTER_ERROR_KEYS,
    unknownErrorKey:
      'page:adminOcctoMaster.submitterMaster.serverError.unknown',
    resolveDetailedError: resolveSubmitterMasterDetailedError,
  })

  useEffect(() => {
    if (isOpen) {
      setValue('validFrom', todayCivilDate())
    }
  }, [isOpen, setValue])

  return (
    <CrudModalLayout
      handleSave={handleSave}
      isDirty={isDirty}
      isOpen={isOpen}
      isPending={isPending}
      onClose={handleClose}
      serverError={serverError}
      // design (`design/admin/occto.html` #sm-edit-modal) は `.ecl-modal--lg`。
      size="lg"
      title={t('page:adminOcctoMaster.submitterMaster.modal.createTitle')}
    >
      <SubmitterMasterFormFields
        control={control}
        errors={errors}
        isEdit={false}
        setValue={setValue}
      />
    </CrudModalLayout>
  )
}
